import React, { useState } from 'react';
import styled from 'styled-components';
import { LogIn, Eye, EyeOff, AlertCircle, UserPlus, KeyRound } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { apiUrl } from '../../config/api';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg);
  position: relative;
  overflow: hidden;
`;

const BgDecor = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: var(--gradient);
  opacity: 0.06;
  filter: blur(80px);
  &:nth-child(1) { top: -100px; left: -100px; }
  &:nth-child(2) { bottom: -150px; right: -100px; width: 500px; height: 500px; }
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 20px;
  padding: 40px;
  width: 380px;
  max-width: 90vw;
  box-shadow: var(--shadow);
`;

const LogoArea = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const LogoImg = styled.img`
  width: 140px;
  height: auto;
  margin-bottom: 8px;
  filter: drop-shadow(0 0 16px var(--primary-alpha, rgba(165, 145, 239, 0.35)));
`;

const AppSubtitle = styled.p`
  font-size: 13px;
  color: var(--text-muted);
  margin: 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  padding-right: ${p => p.$hasToggle ? '42px' : '14px'};
  background: var(--input-bg);
  border: 1px solid ${p => p.$error ? 'var(--error)' : 'var(--input-border)'};
  border-radius: 10px;
  font-size: 14px;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--input-focus);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.12);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const TogglePassword = styled.button`
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  display: flex;
  &:hover { color: var(--primary); }
`;

const ErrorMsg = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--error);
`;

const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  background: var(--gradient);
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  color: var(--btn-text);
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 4px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(165, 145, 239, 0.3);
  }

  &:active { transform: translateY(0); }
  &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 24px;
  font-size: 11px;
  color: var(--text-muted);
`;

const SwitchMode = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  width: 100%;
  text-align: center;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.15s;
  &:hover { background: var(--surface); }
`;

const SuccessMsg = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(34, 197, 94, 0.08);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--success);
`;

const LoginPage = () => {
  const { login, addUser } = useAuth();
  const logoSrc = '/Qlickr-Logo-Normal-Blanc.png';
  const [isRegister, setIsRegister] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setUsername('');
    setDisplayName('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (isResetPassword) {
      if (!username.trim() || !password.trim() || !newPassword.trim()) {
        setError('Veuillez remplir tous les champs');
        return;
      }
      if (newPassword.length < 4) {
        setError('Le nouveau mot de passe doit contenir au moins 4 caractères');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Les nouveaux mots de passe ne correspondent pas');
        return;
      }
      setSubmitting(true);
      try {
        const res = await fetch(apiUrl('/api/auth/reset-password'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: username.trim(), oldPassword: password, newPassword })
        });
        const data = await res.json();
        if (data.success) {
          setSuccess('Mot de passe modifié ! Vous pouvez vous connecter.');
          setTimeout(() => { setIsResetPassword(false); resetForm(); }, 2000);
        } else {
          setError(data.error || 'Erreur lors du reset');
        }
      } catch { setError('Erreur serveur'); }
      setSubmitting(false);
      return;
    }

    if (!username.trim() || !password.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (isRegister) {
      if (password.length < 4) {
        setError('Le mot de passe doit contenir au moins 4 caractères');
        return;
      }
      if (password !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }

      setSubmitting(true);
      try {
        const result = await addUser({
          username: username.trim(),
          password,
          displayName: displayName.trim() || username.trim(),
          role: 'user'
        });
        if (result.success) {
          setSuccess('Compte créé ! Connexion...');
          setTimeout(async () => {
            await login(username.trim(), password);
          }, 800);
        } else {
          setError(result.error);
        }
      } catch { setError('Erreur serveur'); }
      setSubmitting(false);
    } else {
      setSubmitting(true);
      try {
        const result = await login(username.trim(), password);
        if (!result.success) {
          setError(result.error);
        }
      } catch { setError('Erreur serveur'); }
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <BgDecor />
      <BgDecor />
      <Card>
        <LogoArea>
          <LogoImg src={logoSrc} alt="QLICKR" />
          <AppSubtitle>{isResetPassword ? 'Réinitialiser le mot de passe' : isRegister ? 'Créer un nouveau compte' : 'Connectez-vous pour continuer'}</AppSubtitle>
        </LogoArea>

        <Form onSubmit={handleSubmit}>
          <FieldGroup>
            <Label>{isResetPassword ? 'Email du compte' : 'Email ou nom d\'utilisateur'}</Label>
            <InputWrapper>
              <Input
                type="text"
                placeholder="email@exemple.com"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                $error={!!error}
                autoFocus
              />
            </InputWrapper>
          </FieldGroup>

          {isRegister && (
            <FieldGroup>
              <Label>Nom d'affichage (optionnel)</Label>
              <InputWrapper>
                <Input
                  type="text"
                  placeholder="Mon nom"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </InputWrapper>
            </FieldGroup>
          )}

          <FieldGroup>
            <Label>{isResetPassword ? 'Ancien mot de passe' : 'Mot de passe'}</Label>
            <InputWrapper>
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                $error={!!error}
                $hasToggle
              />
              <TogglePassword type="button" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </TogglePassword>
            </InputWrapper>
          </FieldGroup>

          {isResetPassword && (
            <FieldGroup>
              <Label>Nouveau mot de passe</Label>
              <InputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  $error={!!error}
                />
              </InputWrapper>
            </FieldGroup>
          )}

          {(isRegister || isResetPassword) && (
            <FieldGroup>
              <Label>Confirmer {isResetPassword ? 'le nouveau ' : 'le '}mot de passe</Label>
              <InputWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  $error={!!error && (isResetPassword ? newPassword !== confirmPassword : password !== confirmPassword)}
                />
              </InputWrapper>
            </FieldGroup>
          )}

          {error && (
            <ErrorMsg>
              <AlertCircle size={14} />
              {error}
            </ErrorMsg>
          )}

          {success && (
            <SuccessMsg>
              {success}
            </SuccessMsg>
          )}

          <SubmitBtn type="submit" disabled={submitting}>
            {isResetPassword ? <KeyRound size={16} /> : isRegister ? <UserPlus size={16} /> : <LogIn size={16} />}
            {submitting
              ? (isResetPassword ? 'Réinitialisation...' : isRegister ? 'Création...' : 'Connexion...')
              : (isResetPassword ? 'Réinitialiser' : isRegister ? 'Créer le compte' : 'Se connecter')}
          </SubmitBtn>
        </Form>

        <SwitchMode type="button" onClick={() => { setIsRegister(!isRegister); setIsResetPassword(false); resetForm(); }}>
          {isRegister ? 'Déjà un compte ? Se connecter' : 'Pas de compte ? En créer un'}
        </SwitchMode>

        {!isRegister && (
          <SwitchMode type="button" onClick={() => { setIsResetPassword(!isResetPassword); setIsRegister(false); resetForm(); }} style={{ marginTop: 0 }}>
            {isResetPassword ? '← Retour à la connexion' : 'Mot de passe oublié ?'}
          </SwitchMode>
        )}

        {!isRegister && !isResetPassword && (
          <Footer>
            Connectez-vous avec votre compte QLICKR
          </Footer>
        )}
      </Card>
    </Container>
  );
};

export default LoginPage;
